<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { FormKit } from '@formkit/vue';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import SelectInput from 'dashboard/components-next/select/Select.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import BitrixSurveyButtonsConfig from './BitrixSurveyButtonsConfig.vue';
import BitrixHookPickerDialog from './BitrixHookPickerDialog.vue';
import { templateHasButtonsComponent } from 'dashboard/helper/templateHelper';
import {
  buildBitrixSurveyButtonsPayload,
  invalidBitrixSurveyButtonRows,
} from 'dashboard/helper/bitrixSurveyButtonsHelper';

const props = defineProps({
  inbox: { type: Object, required: true },
});

// Bitrix is the only app with `hook_type: account` that expects several hooks per account in
// practice (see apps.yml comment on `bitrix.allow_multiple_hooks`) — it gets its own grouped
// entry + picker dialog below instead of listing every hook loose in the generic select.
const BITRIX_APP_ID = 'bitrix';

const { t } = useI18n();
const store = useStore();

const apps = useMapGetter('integrations/getAppIntegrations');
const uiFlags = useMapGetter('integrations/getUIFlags');
const agents = useMapGetter('agents/getAgents');
const teams = useMapGetter('teams/getTeams');

onMounted(() => {
  store.dispatch('integrations/get');
  store.dispatch('agents/get');
  store.dispatch('teams/get');
});

const selectedHookId = ref('');
const values = reactive({});
const linkToThisInbox = ref(false);
// Kept separate from `values` on purpose — see the comment in BitrixSurveyButtonsConfig.vue's
// props declaration for why survey_buttons can't live inside the FormKit-managed `values` object.
const surveyButtonRows = ref([]);

// Apps that can be connected straight from this generic form (a plain settings form + a
// simple POST), as opposed to apps needing an OAuth redirect (slack/linear/shopify) or a
// different backing model entirely (webhook/dashboard_apps) — those keep using their own
// dedicated screens. Apps flagged `editable_from_list` in apps.yml (e.g. nemesis) have their own
// create/edit screen under Settings > Integrations, so they're not offered here either.
// `hook_type: inbox` apps (e.g. dialogflow) also opt out: they already have their own
// inbox-picker in the "Connect" modal (Settings > Integrations) tied to the native `inbox_id`
// column, which this tab doesn't set.
const isConnectableHere = app =>
  (app.settings_form_schema || []).length > 0 &&
  app.hook_type !== 'inbox' &&
  !app.editable_from_list;

// A hook has no name of its own — when an app allows more than one (e.g. two Bitrix
// integrations for different purposes), disambiguate primarily using `settings.hook_name`
// (the dedicated naming field, currently only Bitrix defines it — see apps.yml), falling back
// to the first `visible_properties` value that's actually set (same fields shown as columns in
// the native multi-hook table at Settings > Integrations) for older hooks/apps without a name,
// and finally to the hook id if nothing else is set yet.
const hookDisambiguator = (app, hook) => {
  if (hook.settings?.hook_name) return hook.settings.hook_name;
  const propertyValue = (app.visible_properties || [])
    .map(property => hook.settings?.[property])
    .find(value => value);
  return propertyValue || `#${hook.id}`;
};

const buildHookEntry = (app, hook) => {
  const baseLabel =
    (app.hooks || []).length > 1
      ? `${app.name} (${hookDisambiguator(app, hook)})`
      : app.name;
  return {
    value: `hook:${hook.id}`,
    label: hook.status
      ? baseLabel
      : `${baseLabel} ${t('INBOX_MGMT.INBOX_INTEGRATIONS.SELECT.DISABLED_HINT')}`,
    app,
    hook,
    isNew: false,
    sortTier: hook.status ? 0 : 1,
  };
};

const buildNewEntry = app => ({
  value: `app:${app.id}`,
  label: `${app.name} ${t('INBOX_MGMT.INBOX_INTEGRATIONS.SELECT.NOT_CONNECTED_HINT')}`,
  app,
  hook: null,
  isNew: true,
  sortTier: 2,
});

// One row per existing hook, plus one "not connected yet" row for every connectable app that
// has none (or that allows more than one, on top of its existing hooks — see
// `app.allow_multiple_hooks` in apps.yml) — so the select always has something to offer, even
// on a fresh account, and multi-hook apps can always add another one. Enabled hooks first, then
// disabled hooks, then not-yet-connected apps; alphabetical within each group.
//
// Bitrix is special-cased into a single grouped entry instead: it's the one app where several
// hooks per account is the norm rather than the exception (see apps.yml), so listing every hook
// loose here would clutter the dropdown — it gets its own picker dialog instead (see
// `BitrixHookPickerDialog.vue` and the `group:` values below).
const integrationOptions = computed(() =>
  (apps.value || [])
    .flatMap(app => {
      if (app.id === BITRIX_APP_ID) {
        if (!isConnectableHere(app)) return [];
        return [
          {
            value: `group:${app.id}`,
            label: app.name,
            app,
            hook: null,
            isNew: false,
            isGroup: true,
            sortTier: (app.hooks || []).length ? 0 : 2,
          },
        ];
      }

      const existingOptions = (app.hooks || []).map(hook =>
        buildHookEntry(app, hook)
      );
      if (!existingOptions.length) {
        if (!isConnectableHere(app)) return [];
        return [buildNewEntry(app)];
      }
      if (app.allow_multiple_hooks && isConnectableHere(app)) {
        return [...existingOptions, buildNewEntry(app)];
      }
      return existingOptions;
    })
    .sort((a, b) => {
      if (a.sortTier !== b.sortTier) return a.sortTier - b.sortTier;
      return a.app.name.localeCompare(b.app.name);
    })
);

const bitrixApp = computed(() =>
  (apps.value || []).find(app => app.id === BITRIX_APP_ID)
);

// `selectedHookId` can point at a specific hook/new-app entry that isn't itself listed in
// `integrationOptions` (e.g. a Bitrix hook chosen from `BitrixHookPickerDialog`, since Bitrix
// hooks are collapsed into a single `group:` option there) — so this resolves directly against
// the raw `apps` list instead of looking the value up inside `integrationOptions`.
const selectedEntry = computed(() => {
  const value = selectedHookId.value;
  if (!value) return null;
  if (value.startsWith('hook:')) {
    const hookId = Number(value.slice('hook:'.length));
    const owner = (apps.value || [])
      .flatMap(app => (app.hooks || []).map(hook => ({ app, hook })))
      .find(({ hook }) => hook.id === hookId);
    return owner ? buildHookEntry(owner.app, owner.hook) : null;
  }
  if (value.startsWith('app:')) {
    const appId = value.slice('app:'.length);
    const app = (apps.value || []).find(item => item.id === appId);
    return app ? buildNewEntry(app) : null;
  }
  // `group:*` (the Bitrix picker trigger) never resolves to a real selection on its own.
  return null;
});

// What the select box itself should show as "picked" — for Bitrix this is always the grouped
// entry, even once a specific hook has been chosen via the picker dialog, since that hook isn't
// one of the select's own options.
const displaySelectValue = computed({
  get: () => {
    if (selectedEntry.value?.app?.id === BITRIX_APP_ID) {
      return `group:${BITRIX_APP_ID}`;
    }
    return selectedHookId.value;
  },
  set: value => {
    selectedHookId.value = value;
  },
});

// Tracks whether the Bitrix picker dialog was closed because the admin picked something
// (existing hook or "create new"), as opposed to dismissing it (Escape/click-outside/no
// button) — only the latter should clear the selection back to the placeholder.
let bitrixPickerHandled = false;

const bitrixPickerRef = ref(null);

watch(selectedHookId, value => {
  if (value?.startsWith('group:')) {
    bitrixPickerHandled = false;
    bitrixPickerRef.value?.open();
  }
});

const handleBitrixHookSelected = hook => {
  bitrixPickerHandled = true;
  selectedHookId.value = `hook:${hook.id}`;
};

const handleBitrixCreateNew = () => {
  bitrixPickerHandled = true;
  if (bitrixApp.value) selectedHookId.value = `app:${bitrixApp.value.id}`;
};

const handleBitrixPickerClosed = () => {
  if (!bitrixPickerHandled) {
    selectedHookId.value = '';
  }
};

// Editing an existing hook only shows fields listed in the app's `visible_properties` (see
// `_hook.json.jbuilder`) — secrets like `bridge_token`/`webhook_url` are stripped out before
// they ever reach the frontend, so rendering them here would show them blank and, on save,
// wipe the real saved value. A brand-new (not yet connected) app has no saved secrets to
// protect, so it shows every field, same as the main "Connect" modal.
const formSchema = computed(() => {
  const entry = selectedEntry.value;
  if (!entry) return [];
  if (entry.isNew) return entry.app.settings_form_schema || [];
  const visibleProperties = entry.app.visible_properties || [];
  return (entry.app.settings_form_schema || []).filter(item =>
    visibleProperties.includes(item.name)
  );
});

const whatsappTemplates = computed(
  () =>
    store.getters['inboxes/getFilteredWhatsAppTemplates'](props.inbox.id) || []
);

const templateOptions = computed(() => {
  const names = [
    ...new Set(whatsappTemplates.value.map(template => template.name)),
  ];
  return names.map(name => ({ label: name, value: name }));
});

const agentOptions = computed(() =>
  (agents.value || []).map(agent => ({ label: agent.name, value: agent.id }))
);

const teamOptions = computed(() =>
  (teams.value || []).map(team => ({ label: team.name, value: team.id }))
);

const isBitrixApp = computed(() => selectedEntry.value?.app?.id === 'bitrix');

// entity/status_field/status_field_shared/survey_buttons only make sense once a WhatsApp
// template with actual reply buttons is selected (rendered instead by
// <BitrixSurveyButtonsConfig>, a 2-tab widget — see that component). status_contactado/
// status_no_contactado are the old plain-text SI/NO fields, fully replaced by survey_buttons: a
// template with no buttons (e.g. the sandbox `hello_world`) isn't expected to get a real survey
// reply at all, so none of these are shown or required for it
// (Integrations::Hook#validate_bitrix_settings only requires anything when `survey_buttons` is
// configured — see HIDDEN_HINT below for what's shown instead).
const BITRIX_MANAGED_FIELD_NAMES = [
  'entity',
  'status_field_shared',
  'status_field',
  'survey_buttons',
  'status_contactado',
  'status_no_contactado',
];

// Matched by name + language when both are set (a portal can have several language variants of
// the same template name); falls back to name-only so the widget still resolves a template
// while the admin is mid-edit and hasn't picked/confirmed a language yet.
const selectedBitrixTemplate = computed(() => {
  if (!isBitrixApp.value || !values.survey_template_name) return null;
  const byNameAndLanguage = whatsappTemplates.value.find(
    template =>
      template.name === values.survey_template_name &&
      (!values.survey_template_language ||
        template.language === values.survey_template_language)
  );
  return (
    byNameAndLanguage ||
    whatsappTemplates.value.find(
      template => template.name === values.survey_template_name
    ) ||
    null
  );
});

// The button-config widget only makes sense once a template with an actual BUTTONS component
// is selected — otherwise there's nothing for the buttons to reply to. Hidden (not cleared):
// switching away from a buttons-template and back must restore whatever was configured before,
// so we never touch `values.entity`/`status_field*`/`survey_buttons` here.
const showBitrixSurveyWidget = computed(
  () =>
    isBitrixApp.value &&
    !!selectedBitrixTemplate.value &&
    templateHasButtonsComponent(selectedBitrixTemplate.value)
);

const visibleFormItems = computed(() =>
  formSchema.value
    .filter(
      item =>
        !(isBitrixApp.value && BITRIX_MANAGED_FIELD_NAMES.includes(item.name))
    )
    .map(item => {
      if (item.name === 'survey_template_name') {
        return {
          ...item,
          type: 'select',
          options: templateOptions.value,
          placeholder: t(
            'INBOX_MGMT.INBOX_INTEGRATIONS.TEMPLATE_FIELD.PLACEHOLDER'
          ),
        };
      }
      if (item.name === 'assigned_agent_id') {
        return { ...item, type: 'select', options: agentOptions.value };
      }
      if (item.name === 'assigned_team_id') {
        return { ...item, type: 'select', options: teamOptions.value };
      }
      return item;
    })
);

// The FormKit item schemas for entity/status_field, handed down to BitrixSurveyButtonsConfig so
// it can render them with the same label/help copy apps.yml already defines for them, instead of
// duplicating that copy in this JS bundle.
const bitrixEntityField = computed(() =>
  formSchema.value.find(item => item.name === 'entity')
);
const bitrixStatusFieldField = computed(() =>
  formSchema.value.find(item => item.name === 'status_field')
);

const linkedInboxId = computed(() => {
  const raw = selectedEntry.value?.hook?.settings?.linked_inbox_id;
  return raw ? Number(raw) : null;
});

const isLinkedToThisInbox = computed(
  () => !!linkedInboxId.value && linkedInboxId.value === Number(props.inbox.id)
);

const linkedToOtherInboxName = computed(() => {
  if (!linkedInboxId.value || isLinkedToThisInbox.value) return '';
  const otherInbox = store.getters['inboxes/getInbox'](linkedInboxId.value);
  return otherInbox?.name || '';
});

const resetFormState = () => {
  Object.keys(values).forEach(key => delete values[key]);
  if (selectedEntry.value && !selectedEntry.value.isNew) {
    Object.assign(values, selectedEntry.value.hook.settings || {});
  }
  // survey_buttons is intentionally not part of `values` (see BitrixSurveyButtonsConfig.vue) —
  // strip whatever Object.assign just copied in from the raw hook settings and seed the
  // separate ref instead.
  delete values.survey_buttons;
  const existingButtons = selectedEntry.value?.hook?.settings?.survey_buttons;
  surveyButtonRows.value = Array.isArray(existingButtons)
    ? existingButtons
    : [];
  linkToThisInbox.value = isLinkedToThisInbox.value;
};

watch(selectedHookId, resetFormState);

const isSaving = computed(
  () => uiFlags.value.isUpdatingHook || uiFlags.value.isCreatingHook
);

const saveButtonLabel = computed(() =>
  selectedEntry.value?.isNew
    ? t('INBOX_MGMT.INBOX_INTEGRATIONS.CONNECT')
    : t('INBOX_MGMT.INBOX_INTEGRATIONS.SAVE')
);

// slugifyButtonLabel/buildBitrixSurveyButtonsPayload now live in
// dashboard/helper/bitrixSurveyButtonsHelper.js, shared with NewHook.vue (Settings >
// Integrations create-hook flow), which needs the exact same key-derivation logic.

const saveSettings = async () => {
  if (!selectedEntry.value) return;

  const settings = { ...values };
  if (linkToThisInbox.value) {
    settings.linked_inbox_id = Number(props.inbox.id);
  } else if (!selectedEntry.value.isNew && isLinkedToThisInbox.value) {
    settings.linked_inbox_id = null;
  }

  // `values` never carries survey_buttons (see resetFormState) — build it from the separate ref.
  if (isBitrixApp.value) {
    if (showBitrixSurveyWidget.value) {
      // Los campos del widget de botones van con `ignore` (ver BitrixSurveyButtonsConfig.vue),
      // así que el form nativo no los valida solo -- se chequea acá antes de mandar nada, para
      // no guardar una fila a medias que después le manda un campo vacío a Bitrix.
      const incompleteRows = invalidBitrixSurveyButtonRows(
        surveyButtonRows.value
      );
      if (incompleteRows.length) {
        useAlert(
          t(
            'INBOX_MGMT.INBOX_INTEGRATIONS.BITRIX_SURVEY.BUTTONS_TAB.INCOMPLETE_ROWS_ERROR',
            { rows: incompleteRows.join(', ') }
          )
        );
        return;
      }

      const payload = buildBitrixSurveyButtonsPayload(surveyButtonRows.value);
      if (payload) {
        settings.survey_buttons = payload;
        // There's no more shared/global toggle in the UI — every button always carries its own
        // status_field (see BitrixSurveyButtonsConfig), so the backend should always resolve
        // each button independently rather than falling back to the top-level status_field.
        settings.status_field_shared = false;
      }
    } else if (!selectedEntry.value.isNew) {
      // Widget hidden (no buttons-template selected right now) — re-attach whatever the hook
      // already had saved, unchanged, so this save can't silently wipe it (see
      // showBitrixSurveyWidget: hidden means "don't touch", not "clear").
      const existingButtons = selectedEntry.value.hook.settings?.survey_buttons;
      if (Array.isArray(existingButtons) && existingButtons.length) {
        settings.survey_buttons = existingButtons;
      }
    }
  }

  try {
    if (selectedEntry.value.isNew) {
      const created = await store.dispatch('integrations/createHook', {
        app_id: selectedEntry.value.app.id,
        settings,
      });
      selectedHookId.value = `hook:${created.id}`;
    } else {
      await store.dispatch('integrations/updateHook', {
        hookId: selectedEntry.value.hook.id,
        settings,
      });
      await store.dispatch('integrations/get');
    }
    useAlert(t('INBOX_MGMT.INBOX_INTEGRATIONS.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.INBOX_INTEGRATIONS.API.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <div class="mx-6 max-w-4xl">
    <div class="mb-5">
      <h3 class="text-heading-3 text-n-slate-12">
        {{ $t('INBOX_MGMT.INBOX_INTEGRATIONS.TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.INBOX_INTEGRATIONS.SUBTITLE') }}
      </p>
    </div>

    <div class="flex flex-col gap-5">
      <WithLabel
        class="w-full max-w-xs"
        :label="$t('INBOX_MGMT.INBOX_INTEGRATIONS.SELECT.LABEL')"
        name="integration-hook"
      >
        <SelectInput
          v-model="displaySelectValue"
          :options="integrationOptions"
          :placeholder="$t('INBOX_MGMT.INBOX_INTEGRATIONS.SELECT.PLACEHOLDER')"
        />
      </WithLabel>

      <BitrixHookPickerDialog
        v-if="bitrixApp"
        ref="bitrixPickerRef"
        :app="bitrixApp"
        :inbox="inbox"
        @select="handleBitrixHookSelected"
        @create="handleBitrixCreateNew"
        @close="handleBitrixPickerClosed"
      />

      <div
        v-if="selectedEntry"
        :key="selectedEntry.value"
        class="flex flex-col gap-4 p-4 rounded-xl outline outline-1 outline-n-weak"
      >
        <p
          v-if="selectedEntry.isNew"
          class="text-xs font-medium text-n-slate-11"
        >
          {{ $t('INBOX_MGMT.INBOX_INTEGRATIONS.STATUS.NOT_CONNECTED') }}
        </p>
        <p
          v-else
          class="text-xs font-medium"
          :class="isLinkedToThisInbox ? 'text-n-teal-11' : 'text-n-amber-11'"
        >
          <template v-if="isLinkedToThisInbox">
            {{
              $t('INBOX_MGMT.INBOX_INTEGRATIONS.STATUS.LINKED_TO_THIS_INBOX')
            }}
          </template>
          <template v-else-if="linkedToOtherInboxName">
            {{
              $t('INBOX_MGMT.INBOX_INTEGRATIONS.STATUS.LINKED_TO_OTHER_INBOX', {
                inboxName: linkedToOtherInboxName,
              })
            }}
          </template>
          <template v-else>
            {{ $t('INBOX_MGMT.INBOX_INTEGRATIONS.STATUS.NOT_LINKED') }}
          </template>
        </p>

        <FormKit v-model="values" type="form" :actions="false">
          <FormKit
            v-for="item in visibleFormItems"
            :key="item.name"
            v-bind="item"
          />

          <BitrixSurveyButtonsConfig
            v-if="showBitrixSurveyWidget"
            v-model:survey-buttons="surveyButtonRows"
            :values="values"
            :template="selectedBitrixTemplate"
            :entity-field="bitrixEntityField"
            :status-field-field="bitrixStatusFieldField"
          />
          <p
            v-else-if="isBitrixApp"
            class="text-xs text-n-slate-11 p-3 rounded-lg outline outline-1 outline-n-weak"
          >
            {{ $t('INBOX_MGMT.INBOX_INTEGRATIONS.BITRIX_SURVEY.HIDDEN_HINT') }}
          </p>
        </FormKit>

        <label class="flex items-center gap-2 text-sm text-n-slate-12">
          <input v-model="linkToThisInbox" type="checkbox" />
          {{ $t('INBOX_MGMT.INBOX_INTEGRATIONS.LINK_CHECKBOX.LABEL') }}
        </label>
        <p class="text-xs text-n-slate-11 -mt-2">
          {{ $t('INBOX_MGMT.INBOX_INTEGRATIONS.LINK_CHECKBOX.HELP') }}
        </p>

        <div class="flex justify-end">
          <NextButton
            :label="saveButtonLabel"
            :is-loading="isSaving"
            @click="saveSettings"
          />
        </div>
      </div>
    </div>
  </div>
</template>
